import { AfterViewChecked, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { Note } from "ng/src/app/notes/shared/note.model";
import { combineLatest, of, race, Subject } from "rxjs";
import { TemplateSegment, TemplateSegmentTypeId, TemplateTypeId } from "ng/src/app/templates/shared/template.model";
import { map, skip, startWith, take, takeUntil } from "rxjs/operators";
import { AutoSyncType, CalendarControl, CalendarEventAction, EditorMode, NoteEntryGroup, NoteEntryInput, OverflowMenuItem } from "../shared/note-entry.model";
import { NoteEntryService } from "../shared/note-entry.service";
import { AppStateService } from "ng/src/app/core/services/app-state.service";
import { ConfirmationService, MenuItem } from "primeng/api";
import { UntypedFormControl } from "@angular/forms";
import { Calendar } from "primeng/calendar";

@Component({
	templateUrl: './note-entry.component.html',
	styleUrls: ['./note-entry.component.scss'],
	selector: 'lb-note-entry'
})
export class NoteEntryComponent implements OnInit, OnDestroy, AfterViewChecked {
	@Input() mode: EditorMode = EditorMode.EDITING;
	@Input() scrollParent = true;
	
	@Output() modeChange = new EventEmitter<EditorMode>();
	@Output('noteChanged') noteChanged = new EventEmitter<Note>();
	
	readonly SegmentTypes = TemplateSegmentTypeId;
	readonly EditorModes = EditorMode;
	
	groups: NoteEntryGroup[] = [];
	controls: { [segmentId: number]: NoteEntryInput } = {};
	overflowMenu: {[segmentId: number]: MenuItem[] } = {};
	selectedGroup?: NoteEntryGroup;
	
	calcDialogVisible = false;
	mobileOverlayText?: string;
	
	get properties() {
		return this.noteEntryService.noteProperties;
	}
	
	private destroy$ = new Subject();
	
	get selectedControls() {
		return this.selectedGroup?.controls ?? [];
	}
	
	get mobileView() {
		return !AppStateService.hoverSupported;
	}
	
	get noteModel() {
		return this.properties?.note?.model ?? '';
	}
	set noteModel(val) {
		if (this.properties?.note)
			this.properties.note.model = val;
	}
	
	get useRichText() {
		return this.properties?.note?.useRichText ?? true;
	}
	set useRichText(val) {
		if (this.properties?.note)
			this.properties.note.useRichText = val;
	}
	
	get syncingNote() {
		return this.noteEntryService.isSyncingNote;
	}
	
	get note() {
		return this.noteEntryService.editingNote;
	}
	
	get templateIsBasicNote() {
		return this.noteEntryService.selectedTemplate?.isDefaultTemplate ?? false;
	}
	
	get calculationErrors() {
		return this.noteEntryService.calculationErrors;
	}
	
	private needsFocus = false;
	
	constructor(
		public noteEntryService: NoteEntryService,
		private confirmService: ConfirmationService,
		private cd: ChangeDetectorRef,
		private elRef: ElementRef
	) {}

	ngOnInit(): void {
		this.noteEntryService.selectedGroup$.pipe(startWith(this.selectedGroup),takeUntil(this.destroy$)).subscribe((group: NoteEntryGroup | undefined) => {
			this.selectedGroup = group;
			this.needsFocus = true;
			
			this.selectedGroup?.controls?.forEach(control => {
				this.overflowMenu[control.templateSegmentId]?.forEach((i: MenuItem) => (i as OverflowMenuItem).destroy?.());
				
				const commentMenu: MenuItem = (() => {
					const commentControlHasValue = () => control.comment.length > 0;
					const attachmentControlHasValue = () => control.commentAttachments.length > 0;
					const getIcon = () => control.displayingComments ? 'pi pi-check' : '';
					const getDisabled = () => this.noteEntryService.selectedTemplateType === TemplateTypeId.OPERATIONAL || (
						control.displayingComments && (control.commentError || commentControlHasValue() || attachmentControlHasValue())
					);
					const _destroy$ = new Subject();
					
					const item: OverflowMenuItem = {
						label: 'Comment',
						icon: getIcon(),
						disabled: getDisabled(),
						destroy: () => _destroy$.next(),
						command: () => {
							control.displayingComments = !control.displayingComments;
							update();
						}
					}
					
					control.onValidation$.pipe(
						takeUntil(
							race(
								_destroy$,
								this.destroy$
							))
						)
						.subscribe(values => {
							update();
						})
					
					update();
					
					return item;
					
					function update() {
						if (control.commentError || control.comment.length) {
							control.displayingComments = true;
						}
						
						item.disabled = getDisabled();
						item.icon = getIcon();
					}
				})();
				
				const pastValuesMenu: MenuItem = (() => {
					const isDisabled = () => !this.noteEntryService.editingNote;
					const getIcon = () => control.displayingPastValues ? 'pi pi-check' : '';
					
					const valueMenu: MenuItem = {
						label: 'Past Values',
						command: () => control.displayingPastValues = !control.displayingPastValues,
						icon: getIcon(),
						disabled: isDisabled()
					}
					
					combineLatest([
						this.noteEntryService.editingNote$.pipe(skip(1)),
						control.displayingPastValues$.pipe(startWith(control.displayingPastValues))
					])
					.pipe(
						takeUntil(this.noteEntryService.selectedTemplate$.pipe(skip(1)))
					)
						.subscribe(() => {
							update();
						})
					
					
					function update() {
						valueMenu.icon = getIcon();
						valueMenu.disabled = isDisabled();
					}
					
					return valueMenu;
				})();
				
				const menuItems: MenuItem[] = [commentMenu];
				if (!control.templateSegment.key)
					menuItems.push(pastValuesMenu)
				
				this.overflowMenu[control.templateSegmentId] = menuItems;
			})
		});
	}
	
	ngAfterViewChecked(): void {
		this.noteEntryService.checkForValueChanges();
		this.cd.detectChanges();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
	}

	onBasicNoteUpdate(update: { text: string, useRichText: boolean }) {
		this.noteModel = update.text;
		this.useRichText = update.useRichText;
		this.noteEntryService.triggerSync(AutoSyncType.AUTO);
	}
	
	shouldShowSelectorFilter(input: NoteEntryInput) {
		const initSizes = this.noteEntryService.initialSizes;
		let size = 0;
		switch (input.templateSegmentType) {
			case TemplateSegmentTypeId.TEXT_CHOICE:
				size = initSizes.choiceLists[input.templateSegment.choiceList!.id] ?? 0;
				break;
			case TemplateSegmentTypeId.TAG_CHOICE:
				size = initSizes.tagValues[input.templateSegmentId] ?? 0;
				break;
			case TemplateSegmentTypeId.USER_CHOOSER:
				size = initSizes.logbookUsers[input.logId] ?? 0;
				break;
		}
		return size >= 25;
	}
	
	getSegmentHTMLId(segment: TemplateSegment): string {
		return `segment-${segment.id}`;
	}
	
	getRangeText(segment: TemplateSegment): string {
		const min = segment.minRequired ?? 0;
		const max = segment.maxAllowed ?? 0;
		
		if (min === 0 && max === 0) {
			return '';
		}
		else if (min === max) {
			return `\n(${min})`;
		}
		else if (max > 0) {
			return `\n(${min}-${max})`;
		}
		else if (min > 0) {
			return `\n(${min}+)`;
		}
		return '';
	}
	
	focus(input: any) {
		if (!this.needsFocus)
			return;
		
		const el = typeof input === 'string' ? document.getElementById(input) : input;
		this.needsFocus = false;
		
		const options: FocusOptions = {
			preventScroll: true
		};
		
		setTimeout(() => {
			if (el?.focus) {
				el?.focus?.(options);
			}
			else if (el?.nativeElement) {
				el?.nativeElement?.focus?.(options);
			}
		})
	}
	
	showCalcOverlay(event: Event, input: NoteEntryInput) {
		this.mobileOverlayText = this.getToolTip(input.templateSegmentId);
		
		if (!this.mobileView || !this.mobileOverlayText)
			return;
		
		this.calcDialogVisible = true;
	}
	
	getToolTip(segmentId: number) {
		return this.noteEntryService.calculationTooltips[segmentId] ?? ''
	}
	
	setCalendarValue(calendar: Calendar, input: NoteEntryInput, value: Date | null | 'now') {
		const control = this.getCalendarControl(calendar, input);
		value = (value === 'now') ? new Date() : value;
		control.control.setValue(value);
	}
	
	getCalendarControl(calendar: Calendar, input: NoteEntryInput) {
		const segmentId = input.templateSegmentId;
		const control = this.noteEntryService.calendarControls[segmentId];
		const formControl = new UntypedFormControl(null);
		
		if (control)
			return control?.updateCalendar(calendar);
		else if (input.templateSegment.calculate) {
			// Uses 'onValidation' event for checking for calculation updates as regular value updates are not emitted for calculation fields
			input.onValidation$.pipe(
				takeUntil(this.destroy$)
			).subscribe((val) => {
				formControl.setValue(input.value);
			});
		}
		
		return (this.noteEntryService.calendarControls[segmentId] = new CalendarControl(calendar, formControl, this.noteEntryService.destroy$))
			.setOnCloseHandler((delegate) => {
				if (!delegate.isInvalid) return of(CalendarEventAction.LEAVE);
				
				const shouldContinue = new Subject<CalendarEventAction>();
				this.confirmService.confirm({
					key: 'invalidDateDialog',
					message: 'The current input date is invalid. Continue editing?',
					defaultFocus: 'accept',
					rejectButtonStyleClass: 'p-button-secondary',
					reject: () => {
						shouldContinue.next(CalendarEventAction.REVERT);
					},
					accept: () => {
						shouldContinue.next(CalendarEventAction.FOCUS);
					}
				})
				return shouldContinue.pipe(take(1));
			})
			.setOnFinalizeHandler((value => {
				if (value === input.value) return;
				input.value = value;
			}))
			.setValueProvider(
				input.onValidation$.pipe(
					startWith(<null | Date>input.value),
					map(() => input.value)
				)
			)
	}
}

